import { useEffect } from "react";
import axios, { AxiosHeaderValue } from "axios";
import Cookie from "js-cookie";
import { useAuth } from "@/providers/auth.provider";

export type apiPageOptions = {
  page?: number;
  order?: string;
  take?: number;
};

const axiosClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  timeout: 10000,
});

export const useAxios = (
  options: {
    headers?: [{ [key: string]: AxiosHeaderValue }];
    timeout?: number;
  } = {},
) => {
  const { login, logout } = useAuth() as any;
  // add headers
  if (options.headers) {
    options.headers.forEach((header) => {
      Object.keys(header).forEach((key) => {
        axiosClient.defaults.headers.common[key] = header[key];
      });
    });
  }

  if (options.timeout) {
    axiosClient.defaults.timeout = options.timeout;
    console.log(`Axios timeout set to ${options.timeout} ms`);
  }

  useEffect(() => {
    // Add a request interceptor
    const requestInterceptor = axiosClient.interceptors.request.use(
      function (config) {
        // Do something before the request is sent
        // For example, add an authentication token to the headers
        // set timezone to the headers using a standard header name
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const token = Cookie.get("access_token"); // Get the token from the cookie
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          config.headers["timezone"] = timezone;
        }

        return config;
      },
      function (error) {
        // Handle the error
        return Promise.reject(error);
      },
    );

    // Add a response interceptor
    const responseInterceptor = axiosClient.interceptors.response.use(
      function (response) {
        // Any status code that lie within the range of 2xx cause this function to trigger
        return response;
      },
      function (error) {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        if (error.response && error.response.status === 401) {
          // Perform logout action
          logout();
        }
        return Promise.reject(error);
      },
    );

    // Clean up the interceptors when the component unmounts
    return () => {
      axiosClient.interceptors.request.eject(requestInterceptor);
      axiosClient.interceptors.response.eject(responseInterceptor);
    };
  }, [login, logout]);

  return axiosClient;
};
